import { type MouseEvent, useEffect, useState } from "react";

import { usePostCacheContext } from "@/contexts/post-cache-context";
import { usePostContext } from "@/contexts/post-context";
import { useEnterKeyTrigger } from "@/hooks/use-handle-keydown";
import { feedFreezeDebugLog } from "@/utils/feed-freeze-debug";
import { cn } from "@/utils/lib";
import { MIN_CHAR_TO_SHOW } from "@/utils/post-utils";

type PostBodyProps = {
  className?: string;
  isMarkdown?: boolean;
  body?: string | null;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
};

export const PostBody = ({
  className = "",
  isMarkdown = true,
  onClick,
  body,
}: PostBodyProps) => {
  const { postHash, postCachePrefix } = usePostContext();

  const cacheKey = body || postHash;
  const { postBodyMDCache, schedulePostBodyCacheWarmup } = usePostCacheContext(
    postHash,
    postCachePrefix,
  );

  const hasShortBody = body && body.length > MIN_CHAR_TO_SHOW;
  const [cacheRecord, setCacheRecord] = useState(() =>
    postBodyMDCache.get(cacheKey),
  );

  useEffect(() => {
    if (!body) {
      setCacheRecord(undefined);
      return;
    }

    const existingRecord = postBodyMDCache.get(cacheKey);
    if (existingRecord) {
      setCacheRecord(existingRecord);
      return;
    }

    setCacheRecord(undefined);

    return schedulePostBodyCacheWarmup({
      cacheKey,
      body: body.trim(),
      isMarkdown,
      priority: "visible",
      onComplete: (record) => {
        setCacheRecord(record);
      },
    });
  }, [
    body,
    cacheKey,
    isMarkdown,
    postBodyMDCache,
    schedulePostBodyCacheWarmup,
  ]);

  const resolvedCacheRecord = cacheRecord;
  const isWarmingBodyCache =
    !!body &&
    (!resolvedCacheRecord || (hasShortBody && !resolvedCacheRecord.shortBody));

  const onKeyDown = useEnterKeyTrigger(onClick);

  useEffect(() => {
    if (!isWarmingBodyCache || (body?.length ?? 0) <= 4000) {
      return;
    }

    feedFreezeDebugLog(
      "post-body",
      "cache:miss",
      {
        bodyLength: body?.length ?? 0,
        cacheKey,
        hasShortBody,
        isMarkdown,
        postHash,
      },
      {
        throttleKey: `post-body-cache:${cacheKey}`,
        throttleMs: 2_000,
      },
    );
  }, [body, cacheKey, hasShortBody, isMarkdown, isWarmingBodyCache, postHash]);

  if (!body) return null;

  return (
    <div
      onClick={onClick}
      onKeyDown={onKeyDown}
      style={{
        overflowWrap: "anywhere",
        whiteSpace: "break-spaces",
        wordBreak: "break-word",
      }}
      className={cn(
        "ProseMirror prose max-w-full min-w-0 text-sm/6 wrap-break-word",
        className,
      )}
    >
      {resolvedCacheRecord?.body ?? body}
    </div>
  );
};
